import React, { useState, useEffect } from 'react';
import './PWAInstallPrompt.css';

/**
 * PWA Install Prompt Component
 * Detects if the app can be installed and shows install prompt
 */
const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator.standalone === true) ||
      document.referrer.includes('android-app://');
    
    setIsStandalone(isInStandaloneMode);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if already shown (localStorage)
    const installPromptShown = localStorage.getItem('pwa-install-prompt-shown');
    const installPromptDismissed = localStorage.getItem('pwa-install-prompt-dismissed');

    // Android: Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt if not dismissed and not shown recently
      if (!installPromptDismissed && !installPromptShown) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // iOS: Show instructions after a delay if not dismissed
    if (iOS && !isInStandaloneMode && !installPromptDismissed && !installPromptShown) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000); // Show after 3 seconds
      return () => {
        clearTimeout(timer);
        window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      };
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Android: Show install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        localStorage.setItem('pwa-install-prompt-shown', 'true');
      } else {
        console.log('User dismissed the install prompt');
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-prompt-dismissed', 'true');
  };

  // Don't show if already in standalone mode or prompt dismissed
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="pwa-install-prompt">
      <div className="pwa-install-prompt-content">
        {isIOS ? (
          <>
            <h3>Install IDI Stream</h3>
            <p>To install this app on your iOS device:</p>
            <ol>
              <li>Tap the <strong>Share</strong> button <span className="ios-icon">⎋</span></li>
              <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
              <li>Tap <strong>"Add"</strong> to confirm</li>
            </ol>
          </>
        ) : (
          <>
            <h3>Install IDI Stream</h3>
            <p>Install this app for a better experience and faster access.</p>
            <button 
              className="pwa-install-button"
              onClick={handleInstallClick}
            >
              Install App
            </button>
          </>
        )}
        <button 
          className="pwa-install-dismiss"
          onClick={handleDismiss}
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;


